import { useState } from "react";
import { toast } from "sonner";
import { PageHero } from "@/components/Bits";
import { SectionLabel } from "@/components/SectionLabel";
import { Field, TextAreaField, SelectField, Honeypot, SuccessScreen, SubmitButton } from "@/components/FormKit";
import { BrochureCTA } from "@/components/BrochureCTA";
import { postJSON } from "@/lib/api";
import { BOARDS, SIZES, ROLES } from "@/data/content";

const TRUST = [
  "Implemented @ The Creator International School",
  "Concept Labs · CoPE-App · IDEA Pedagogy",
  "10,000+ open-ended problems · 1,500+ apparatus",
  "100% board outcomes · Olympiad Rank 1 (twice)",
];

export default function Enroll() {
  const [f, setF] = useState({ school_name: "", board: "", school_size: "", contact_person: "", role: "", city: "", email: "", phone: "", message: "" });
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.board || !f.school_size || !f.role) {
      toast.error("Please complete the board, size and role fields.");
      return;
    }
    setLoading(true);
    try {
      await postJSON("/enroll", { ...f, company });
      setDone(true);
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="enroll-page">
      <PageHero
        n="08"
        label="Enroll Your School"
        lines={["Engage to Learn &", "Learn to Innovate"]}
        intro="Join THECREATOR Education."
      />

      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <SectionLabel n="◆">Why partner with us</SectionLabel>
            <ul className="mt-8 space-y-5">
              {TRUST.map((t, i) => (
                <li key={i} className="flex gap-4 border-b border-white/10 pb-5">
                  <span className="font-mono-label text-xs text-accent">{`0${i + 1}`}</span>
                  <span className="text-white/75 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <BrochureCTA
                label="School Partnership Brochure"
                eyebrow="Downloadable · one-pager"
                note="PBL-TDLE overview, Concept Lab setup, CoPE-App licensing and typical implementation roadmap — for principals & trustees."
                testid="enroll-brochure"
              />
            </div>
          </div>

          <div className="md:col-span-7">
            {done ? (
              <SuccessScreen testid="enroll-success" title="Thank you — we've received your details." body="Our academic team will reach out shortly to discuss bringing PBL-TDLE to your school." />
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="enroll-form">
                <Honeypot value={company} onChange={(e) => setCompany(e.target.value)} />
                <div className="grid sm:grid-cols-2 gap-8">
                  <Field label="School name" id="school_name" value={f.school_name} onChange={set("school_name")} placeholder="e.g. The Creator International School" required />
                  <Field label="City" id="city" value={f.city} onChange={set("city")} placeholder="City" required />
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <SelectField
                    label="Board"
                    id="board"
                    value={f.board}
                    onChange={set("board")}
                    options={BOARDS}
                    placeholder="Select board"
                    colorful
                    palette={["#2E6BB0", "#E14C7A", "#1E9E8A", "#F0A831", "#8B5CF6", "#22C55E", "#F97316", "#94A3B8"]}
                  />
                  <SelectField
                    label="School size"
                    id="school_size"
                    value={f.school_size}
                    onChange={set("school_size")}
                    options={SIZES}
                    placeholder="Number of students"
                    colorful
                    palette={["#94A3B8", "#22C55E", "#1E9E8A", "#2E6BB0", "#F0A831", "#8B5CF6", "#E14C7A"]}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <Field label="Contact person" id="contact_person" value={f.contact_person} onChange={set("contact_person")} placeholder="Your full name" required />
                  <SelectField
                    label="Your role"
                    id="role"
                    value={f.role}
                    onChange={set("role")}
                    options={ROLES}
                    placeholder="Select role"
                    colorful
                    palette={["#F0A831", "#2E6BB0", "#1E9E8A", "#8B5CF6", "#22C55E", "#F97316", "#E14C7A", "#38BDF8", "#EAB308", "#94A3B8"]}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <Field label="Email" id="email" type="email" value={f.email} onChange={set("email")} placeholder="you@school.edu" required />
                  <Field label="Phone" id="phone" value={f.phone} onChange={set("phone")} placeholder="Optional" />
                </div>
                <TextAreaField label="Message" id="message" value={f.message} onChange={set("message")} placeholder="Tell us about your goals…" />
                <SubmitButton loading={loading} testid="enroll-submit">Submit Enrollment</SubmitButton>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
